import { FormInput, FormSelect, Loader } from "@/components";
import { Badge, Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ArrowLeft, Building2, CalendarDays, Lock, Mail, Pencil, Save, ShieldCheck, Star, User, Users, X } from "lucide-react";
import moment from "moment";
import { ReactNode } from "react";
import { FormProvider } from "react-hook-form";
import useOwnershipDetail, { OWNERSHIP_FORM_ID } from "./useOwnershipDetail";

const fmtDate = (v?: string) => (v ? moment(v).format("MM/DD/YYYY") : "—");
const fmtDateTime = (v?: string) => (v ? moment(v).format("MM/DD/YYYY h:mm A") : "—");
const openEnded = (v?: string) => (v ? fmtDate(v) : "Open-ended");
const humanize = (v?: string) => (v ? v.replace(/_/g, " ") : "—");
const yesNo = (v?: boolean) => (v ? "Yes" : "No");

const SOURCE_LABELS: Record<string, string> = { PROPERTY: "Property", OWNER_PRIMARY: "Owner primary", CUSTOM: "Custom" };

const Field = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <div className="text-sm text-gray-900 break-words mt-0.5">{value ?? "—"}</div>
  </div>
);

const HistoryCard = ({ title, headers, rows }: { title: string; headers: string[]; rows: ReactNode[][] }) => (
  <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3 min-w-0">
    <h3 className="font-semibold">{title}</h3>
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>{headers.map((h) => <TableHead key={h} className="font-semibold text-primary">{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((cells, i) => (
            <TableRow key={i}>{cells.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const Counter = ({ label, value, icon, accent }: { label: string; value: ReactNode; icon: ReactNode; accent?: string }) => (
  <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3">
    {icon}
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={cn("text-xl font-semibold", accent)}>{value}</p>
    </div>
  </div>
);

const OwnershipDetail = () => {
  const {
    account: a, isLoading, canEdit, isEditing, startEdit, cancelEdit,
    form, onSubmit, isSaving, stateOptions, onBack, openOwner, openProperty,
  } = useOwnershipDetail();
  const errors: any = form.formState.errors;
  const source = form.watch("correspondenceAddressSource");

  if (isLoading) return <Loader />;
  if (!a) return <div className="p-6 text-gray-500">Ownership not found in the selected community.</div>;

  const primaryContact = a.ownershipType === "JOINT" && a.jointOwners?.length
    ? <ul className="list-none">{a.jointOwners.map((n: string) => <li key={n}>{n}</li>)}</ul>
    : a.primaryContactName;
  const customAddress = a.customAddress
    ? [a.customAddress.addressLine1, a.customAddress.addressLine2, a.customAddress.pobox && `PO Box ${a.customAddress.pobox}`,
       a.customAddress.city, a.customAddress.state, a.customAddress.zipCode].filter(Boolean).join(", ")
    : "—";

  return (
    <FormProvider {...form}>
      <form id={OWNERSHIP_FORM_ID} onSubmit={form.handleSubmit(onSubmit)} className="p-4 md:p-6 flex flex-col gap-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="icon" onClick={onBack} aria-label="Back"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h2 className="text-lg md:text-xl font-semibold">Ownership {a.ownershipCode}</h2>
              <p className="text-sm text-gray-500">Title/customer account header with all linked people records</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button type="button" variant="outline" onClick={cancelEdit} disabled={isSaving}><X className="w-4 h-4" /> Cancel</Button>
                <Button type="submit" disabled={isSaving}><Save className="w-4 h-4" /> Save Changes</Button>
              </>
            ) : canEdit ? (
              <Button type="button" onClick={startEdit}><Pencil className="w-4 h-4" /> Edit</Button>
            ) : <></>}
          </div>
        </div>

        {/* Account card + correspondence */}
        <div className="bg-white rounded-xl border-t-4 border-primary shadow-sm grid grid-cols-1 xl:grid-cols-[1fr_300px]">
          <div className="p-4 md:p-6 flex flex-col gap-5">
            <div className="flex items-start justify-between gap-4 border-b pb-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-primary shrink-0"><Building2 className="w-6 h-6" /></div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Property / Community</p>
                  <button type="button" className="text-lg font-semibold hover:underline text-left" onClick={openProperty}>{a.propertyName || "—"}</button>
                  <p className="text-sm text-gray-500">{a.communityName}</p>
                </div>
              </div>
              <Field label="Property ID" value={a.propertyId} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Field label="Ownership Type" value={a.ownershipType} />
              <Field label="Legal Entity / Trust" value={a.entityName || "—"} />
              <Field label="Start Date" value={fmtDate(a.startDate)} />
              <Field label="End Date" value={openEnded(a.endDate)} />
              <Field label={a.ownershipType === "JOINT" ? "Joint Owners" : "Primary Contact"} value={primaryContact} />
              <Field label="Primary User ID" value={a.primaryUserId
                ? <button type="button" className="text-primary hover:underline" onClick={() => openOwner(a.primaryUserId)}>{a.primaryUserId}</button>
                : "—"} />
              <Field label="Owners on Title" value={a.ownersOnTitle} />
              <Field label="Allocated Ownership" value={`${Number(a.allocatedOwnership || 0)}%`} />
            </div>
            <p className="text-xs text-gray-500 flex items-center gap-1 -mt-2"><Lock className="w-3 h-3" /> Conversion / OCP controlled</p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 border-t pt-4">
              <Field label="Occupancy Type" value={humanize(a.occupancyType)} />
              <Field label="Occupancy Effective From" value={a.occupancyType ? fmtDate(a.occupancyEffectiveFrom) : "—"} />
              <Field label="Occupancy Effective To" value={a.occupancyType ? openEnded(a.occupancyEffectiveTo) : "—"} />
              <div className="hidden lg:block" />

              <Field label="E-Communication Signup" value={yesNo(a.ecommSignedUp)} />
              <Field label="Effective From" value={fmtDate(a.ecommEffectiveFrom)} />
              <Field label="Effective To" value={openEnded(a.ecommEffectiveTo)} />
              <div className="hidden lg:block" />

              <Field label="Legal Action" value={yesNo(a.legalAction)} />
              <Field label="Effective From" value={fmtDate(a.legalActionEffectiveFrom)} />
              <Field label="Effective To" value={openEnded(a.legalActionEffectiveTo)} />
              <div className="hidden lg:block" />

              <Field label="Lien Exercised" value={yesNo(a.lienExercised)} />
              <Field label="Effective From" value={fmtDate(a.lienEffectiveFrom)} />
              <Field label="Effective To" value={openEnded(a.lienEffectiveTo)} />
            </div>
          </div>

          <div className="bg-gray-50 xl:border-l p-4 md:p-6 flex flex-col gap-4 rounded-b-xl xl:rounded-bl-none xl:rounded-r-xl">
            <h3 className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Correspondence</h3>
            {isEditing ? (
              <>
                <FormSelect name="correspondenceAddressSource" control={form.control} label="Address Source"
                  options={Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }))} />
                <FormSelect name="distribution" control={form.control} label="Distribution" options={[
                  { label: "All owners on this title", value: "ALL" },
                  { label: "Primary contact only", value: "PRIMARY" },
                ]} />
                {source === "CUSTOM" ? (
                  <div className="flex flex-col gap-3">
                    <FormInput name="customAddress.addressLine1" control={form.control} label="Address Line 1" isRequired placeholder="Please enter" error={errors.customAddress?.addressLine1?.message} />
                    <FormInput name="customAddress.addressLine2" control={form.control} label="Address Line 2" placeholder="Please enter" />
                    <FormInput name="customAddress.pobox" control={form.control} label="PO Box" placeholder="Please enter" />
                    <FormInput name="customAddress.city" control={form.control} label="City" isRequired placeholder="Please enter" error={errors.customAddress?.city?.message} variant="char" />
                    <FormSelect name="customAddress.state" control={form.control} label="State" isRequired placeholder="Please select" options={stateOptions} error={errors.customAddress?.state?.message} />
                    <FormInput name="customAddress.zipCode" control={form.control} label="ZIP Code" isRequired placeholder="XXXXX or XXXXX-XXXX" maxLength={10} variant="integerSpeChar" error={errors.customAddress?.zipCode?.message} />
                  </div>
                ) : <></>}
              </>
            ) : (
              <>
                <Field label="Address Source" value={SOURCE_LABELS[a.correspondenceAddressSource] || a.correspondenceAddressSource} />
                <Field label="Distribution" value={a.multiCorrespondence ? "All owners on this title" : "Primary contact only"} />
                <Field label="Custom Address" value={customAddress} />
              </>
            )}
            <Field label="E-Communication" value={a.ecommSignedUp ? "Enrolled" : "Not enrolled"} />
            <div className="grid grid-cols-2 gap-4 border-t pt-4 mt-auto">
              <Field label="Created" value={fmtDateTime(a.createdAt)} />
              <Field label="Last Updated" value={fmtDateTime(a.updatedAt)} />
            </div>
          </div>
        </div>

        {/* History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <HistoryCard title="Occupancy history" headers={["Occupancy Type", "Effective From", "Effective To"]}
            rows={(a.occupancyHistory || []).length
              ? a.occupancyHistory.map((p: any) => [humanize(p.value), fmtDate(p.effectiveFrom), openEnded(p.effectiveTo)])
              : [["—", "—", "—"]]} />
          <HistoryCard title="E-communication audit trail" headers={["Signed Up", "Effective From", "Effective To"]}
            rows={(a.ecommHistory || []).map((p: any) => [p.value === "YES" ? "Yes" : "No", fmtDate(p.effectiveFrom), openEnded(p.effectiveTo)])} />
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Counter label="People linked" value={a.peopleLinked} icon={<Users className="w-5 h-5 text-primary" />} />
          <Counter label="Owners" value={a.owners} icon={<ShieldCheck className="w-5 h-5 text-primary" />} />
          <Counter label="Tenants" value={a.tenants} icon={<User className="w-5 h-5 text-purple-600" />} />
          <Counter label="Share control" value={`${Number(a.allocatedOwnership || 0)}%`} accent="text-green-700"
            icon={<CalendarDays className="w-5 h-5 text-green-700" />} />
        </div>

        {/* Owners and tenants */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col gap-3">
          <div>
            <h3 className="text-lg font-semibold">Owners and tenants</h3>
            <p className="text-sm text-gray-500">
              Owner rows are read-only and controlled by conversion/OCP. Occupancy is derived from the active owners and tenants on this title.
            </p>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Line", "Owner / User ID", "Person", "Relationship", "Title Share", "Effective Period", "Status"]
                    .map((h) => <TableHead key={h} className="font-semibold text-primary whitespace-nowrap">{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(a.people || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-gray-500">No people linked to this title.</TableCell></TableRow>
                ) : a.people.map((p: any, i: number) => (
                  <TableRow key={p.propertyUserAssociationId}>
                    <TableCell>{String(i + 1).padStart(2, "0")}</TableCell>
                    <TableCell>
                      <button type="button" className="text-primary font-semibold hover:underline" onClick={() => openOwner(p.userId)}>{p.userId}</button>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        {[p.firstName, p.lastName].filter(Boolean).join(" ") || "—"}
                        {p.primaryUser ? <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" aria-label="Primary" /> : <></>}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-md px-2 py-0.5 text-xs font-semibold">{humanize(p.associationType)}</Badge>
                    </TableCell>
                    <TableCell>{p.sharePercentage != null ? `${Number(p.sharePercentage)}%` : "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmtDate(p.startDate)}{p.endDate ? ` – ${fmtDate(p.endDate)}` : ""}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn(p.active ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-gray-100 text-gray-600", "rounded-md px-2 py-0.5")}>
                        {p.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default OwnershipDetail;
